import React, { useState } from "react";
import "./style.css";
import Heading from "@tds/core-heading";
import Box from "@tds/core-box";
import PropTypes from "prop-types";
import Button from "@tds/core-button";
import FlexGrid from "@tds/core-flex-grid";
import { useTranslation } from "react-i18next";
import { fwaSchema } from "../schema";
import { addDays } from "date-fns";
import FormCol from "../../components/Form/FormCol";
import Notification from "@tds/core-notification";
import Field from "../../components/Form/Field";
import axios from "axios";
import Container from "../../components/Container";
import { Formik, Form } from "formik";
import config from "../../config";
import Spinner from "@tds/core-spinner";
import {
    getBuildingType,
    getBuildingPermission,
    getAppointmentDate,
    getPurchaseDeviceLocation,
    getSmartHubForBusinessProducts,
    getSmarthubAtMoreThanOneLocationOptions
} from "../../utils";
import Tooltip from "@tds/core-tooltip";

const LandingPage = ({ history, location: { search } }) => {
    const [preferredDataSelected, setIsPreferredDate] = useState(false);
    const [t] = useTranslation();
    const { API_BASE } = config;
    const [isLoading, setIsLoading] = useState(false);
    const formikKeys = {
        appointmentDate: "appointmentDate",
        preferredDate1: "preferredDate1",
        preferredDate2: "preferredDate2"
    };
    const initialValues = {
        dealerEmail: "",
        businessName: "",
        streetNumber: "",
        streetName: "",
        unitNumber: "",
        city: "",
        province: "",
        postalCode: "",
        email: "",
        accountNumber: "",
        subscriberNumber: "",
        smartHubForBusinessProduct: "",
        purchaseDeviceLocation: "",
        orderNumber: "",
        onsiteContactName: "",
        onsiteContactNumber: "",
        businessHours: "",
        buildingType: "",
        buildingPermission: "",
        appointmentDate: "",
        preferredDate1: "",
        preferredDate2: "",
        smarthubAtMoreThanOneLocation: "",
        installFeeAcknowledgment: false,
        additionalInfo: ""
    };

    const excludeTime = (date) => {
        if (date !== "") {
            return date.toDateString().split(" ").slice(1).join(" ");
        } else {
            return date;
        }
    };


    const getBusinessAddress = (values) => {
        const unit = values.unitNumber ? `unit ${values.unitNumber}, ` : "";
        return `${values.streetNumber} ${values.streetName}, ${unit}${values.city}, ${values.province}, ${values.postalCode}`;
    };

    const onFormSubmitted = (values) => {
        setIsLoading(true);
        const payload = {
            dealerEmail: values.dealerEmail || null,
            businessName: values.businessName,
            businessAddress: getBusinessAddress(values),
            email: values.email,
            accountNumber: values.accountNumber,
            subscriberNumber: values.subscriberNumber,
            purchaseDeviceLocation: values.purchaseDeviceLocation
                ? values.purchaseDeviceLocation
                : " ",
            smartHubForBusinessProduct: values.smartHubForBusinessProduct
                ? values.smartHubForBusinessProduct
                : " ",
            orderNumber: values.orderNumber,
            onsiteContactName: values.onsiteContactName,
            onsiteContactNumber: values.onsiteContactNumber,
            businessHours: values.businessHours,
            buildingType: values.buildingType ? values.buildingType : " ",
            buildingPermission: values.buildingPermission
                ? values.buildingPermission
                : " ",
            appointmentDate: values.appointmentDate ? values.appointmentDate : " ",
            preferredDate1: excludeTime(values.preferredDate1),
            preferredDate2: excludeTime(values.preferredDate2),
            smarthubAtMoreThanOneLocation: values.smarthubAtMoreThanOneLocation
                ? values.smarthubAtMoreThanOneLocation
                : "",
            additionalInfo: values.additionalInfo
        };

        axios
            .post(`${API_BASE}install-5g-request/create`, payload)
            .then(({ fwaData }) => {
                setIsLoading(false);
                if (fwaData.success === 1) {
                    history.push(`/success`);
                }
            })
            .catch(() => {
                setIsLoading(false);
                history.push(`/success`);
            });
    };

    const handleChange = (event, values, setValues) => {
        const {
            target: { value, name }
        } = event;
        if (name === formikKeys.appointmentDate && value === t("Preferred Dates")) {
            setIsPreferredDate(true);
        }
        if (name === formikKeys.appointmentDate && value === t("Flexible Dates")) {
            setIsPreferredDate(false);
        }
    };

    return (
        <Container>
            <Spinner fullScreen label={t("SUBMITTING_INFO")} spinning={isLoading} />
            <Box between={3} inset={3} padding={100}>
                <Heading level='h2'>{t("LANDING_PAGE_TITLE")}</Heading>
                <p>{t("LANDING_PAGE_MSG_1")}</p>
                <Formik
                    validationSchema={fwaSchema(t)}
                    onSubmit={onFormSubmitted}
                    enableReinitialize
                    initialValues={initialValues}
                >
                    {({ handleSubmit, errors, touched, values, setValues }) => (
                        <Form
                            onSubmit={handleSubmit}
                            onChange={(e) => handleChange(e, values, setValues)}
                        >
                            <Box between={2} inset={3}>
                                <Box>
                                    <Box>
                                        <Notification copy='en' variant='branded' id='fdgSKF'>
                                            <div dangerouslySetInnerHTML={{ __html: t("LANDING_PAGE_MSG_NOTE").replace(/\n/g, '<br/>') }} style={{ color: 'red' }} />
                                        </Notification>
                                        <Field
                                            name='smartHubForBusinessProduct'
                                            type='select'
                                            label={t("SMART_HUB_FOR_BUSINESS_PRODUCT")}
                                            touched={touched}
                                            errors={errors}
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    smartHubForBusinessProduct: e.target.value
                                                })
                                            }
                                            options={getSmartHubForBusinessProducts(t)}
                                            placeholder={t(
                                                "Please select the Smart Hub for Business product"
                                            )}
                                        />
                                        <Field
                                            name='purchaseDeviceLocation'
                                            type='select'
                                            label={t("PURCHASE_DEVICE_LOCATION")}
                                            touched={touched}
                                            errors={errors}
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    purchaseDeviceLocation: e.target.value
                                                })
                                            }
                                            options={getPurchaseDeviceLocation(t)}
                                            placeholder={t(
                                                "Please Select Purchase Device Location"
                                            )}
                                        />
                                        {/* dealerEmail logic */}
                                        {["Picked up in store", "Purchased in store, received via shipping"].includes(values.purchaseDeviceLocation) && (
                                            <Field
                                                name='dealerEmail'
                                                type='email'
                                                label={t("DEALER_EMAIL")}
                                                touched={touched}
                                                errors={errors}
                                                placeholder='Please Enter Email Address'
                                                onChange={(e) =>
                                                    setValues({
                                                        ...values,
                                                        dealerEmail: e.target.value
                                                    })
                                                }
                                            />
                                        )}
                                        {/* orderNumber logic */}
                                        {["Purchased in store, received via shipping", "Shipped directly from TELUS"].includes(values.purchaseDeviceLocation) && (
                                            <Field
                                                name='orderNumber'
                                                label={t("ORDER_NUMBER")}
                                                touched={touched}
                                                errors={errors}
                                                tooltip={
                                                    <Tooltip copy='en'>
                                                        Hint: Found in your order confirmation email e.g.
                                                        ES1223456
                                                    </Tooltip>
                                                }
                                                placeholder='Please Enter Order Number'
                                                onChange={(e) =>
                                                    setValues({
                                                        ...values,
                                                        orderNumber: e.target.value
                                                    })
                                                }
                                            />
                                        )}
                                        {/* All other fields are always shown */}
                                        <Field
                                            name='businessName'
                                            label={t("BUSINESS_NAME")}
                                            touched={touched}
                                            errors={errors}
                                            placeholder='Please Enter Business Name'
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    businessName: e.target.value
                                                })
                                            }
                                        />
                                        <Field
                                            type='Address'
                                            touched={touched}
                                            errors={errors}
                                            values={values}
                                            setValues={setValues}
                                            placeholder='Please Enter Business Address'
                                            readonly={false}
                                            disabled={false}
                                            isSameAs={false}
                                        />
                                        <Field
                                            name='email'
                                            type='email'
                                            label={t("EMAIL")}
                                            touched={touched}
                                            errors={errors}
                                            placeholder='Please Enter Email Address'
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    email: e.target.value
                                                })
                                            }
                                        />
                                        <Field
                                            name='accountNumber'
                                            type='number'
                                            label={t("TELUS_ACCOUNT_NUMBER")}
                                            touched={touched}
                                            errors={errors}
                                            placeholder='Please Enter Account Number'
                                            tooltip={
                                                <Tooltip copy='en'>
                                                    TELUS account number can be found in your contract
                                                </Tooltip>
                                            }
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    accountNumber: e.target.value
                                                })
                                            }
                                        />
                                        <Field
                                            name='subscriberNumber'
                                            type='mask'
                                            CustomField={"hello"}
                                            label={t("TELUS_SUBSCRIBER_NUMBER")}
                                            touched={touched}
                                            errors={errors}
                                            placeholder='Please Enter Subscriber Number'
                                            tooltip={
                                                <Tooltip className='tooltipbar' copy='en'>
                                                    Hint: Phone number associated with Smart Hub for Business account"
                                                </Tooltip>
                                            }
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    subscriberNumber: e.target.value
                                                })
                                            }
                                        />
                                        <Field
                                            name='onsiteContactName'
                                            label={t("ONSITE_CONTACT_NAME")}
                                            touched={touched}
                                            errors={errors}
                                            placeholder='Please Enter Contact Name'
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    onsiteContactName: e.target.value
                                                })
                                            }
                                        />
                                        <Field
                                            name='onsiteContactNumber'
                                            type='mask'
                                            label={t("ONSITE_CONTACT_NUMBER")}
                                            touched={touched}
                                            errors={errors}
                                            placeholder='Please Enter Contact Number'
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    onsiteContactNumber: e.target.value
                                                })
                                            }
                                        />
                                        <Field
                                            name='businessHours'
                                            label={t("BUSINESS_HOURS")}
                                            touched={touched}
                                            errors={errors}
                                            placeholder='Please Enter Business Hours'
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    businessHours: e.target.value
                                                })
                                            }
                                        />
                                        <Field
                                            name='buildingType'
                                            type='select'
                                            label={t("BUILDING_TYPE")}
                                            touched={touched}
                                            errors={errors}
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    buildingType: e.target.value
                                                })
                                            }
                                            options={getBuildingType(t)}
                                            placeholder={t("Please Select Building Type")}
                                        />
                                        <Field
                                            name='buildingPermission'
                                            type='select'
                                            label={t("BUILDING_PERMISSION")}
                                            touched={touched}
                                            errors={errors}
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    buildingPermission: e.target.value
                                                })
                                            }
                                            options={getBuildingPermission(t)}
                                            placeholder={t("Please Select Building Permissions")}
                                        />
                                        <Field
                                            name='appointmentDate'
                                            type='appointmentDateSelect'
                                            label={t("APPOINTMENT_DATE")}
                                            touched={touched}
                                            errors={errors}
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    appointmentDate: e.target.value
                                                })
                                            }
                                            options={getAppointmentDate(t)}
                                            placeholder={t("Please Select Appointment Date")}
                                        />
                                        {preferredDataSelected && (
                                            <>
                                                <Notification copy='en' id='datepicker-notify'>
                                                    Best effort will be taken to accommodate your
                                                    selected dates
                                                </Notification>
                                                <Field
                                                    name='preferredDate1'
                                                    type='datePicker'
                                                    label={t("PREFERRED_DATE1")}
                                                    value={values.preferredDate1}
                                                    touched={touched}
                                                    errors={errors}
                                                    minDate={addDays(new Date(), 7)}
                                                    dateFormat='dd/MM/yyyy'
                                                    placeholder='Please Enter 1st Preferred Date'
                                                    onChange={(date) =>
                                                        setValues({
                                                            ...values,
                                                            [formikKeys.preferredDate1]: date
                                                        })
                                                    }
                                                />
                                                <Field
                                                    name='preferredDate2'
                                                    type='datePicker'
                                                    label={t("PREFERRED_DATE2")}
                                                    value={values.preferredDate2}
                                                    touched={touched}
                                                    errors={errors}
                                                    minDate={addDays(new Date(), 7)}
                                                    dateFormat='dd/MM/yyyy'
                                                    placeholder='Please Enter 2nd Preferred Date'
                                                    onChange={(e) =>
                                                        setValues({
                                                            ...values,
                                                            [formikKeys.preferredDate2]: e
                                                        })
                                                    }
                                                />
                                            </>
                                        )}
                                        <Field
                                            name='smarthubAtMoreThanOneLocation'
                                            type='select'
                                            label={t("SMART_HUB_AT_MORE_THAN_ONE_LOCATION")}
                                            touched={touched}
                                            errors={errors}
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    smarthubAtMoreThanOneLocation: e.target.value
                                                })
                                            }
                                            options={getSmarthubAtMoreThanOneLocationOptions(t)}
                                            placeholder={t(
                                                "Please choose if you are installing your Smart Hub at more than one office location"
                                            )}
                                        />
                                        {values.smarthubAtMoreThanOneLocation === 'Yes' && (
                                            <Notification copy='en' id="more-than-one-notify">
                                                NOTE: Please submit this form again for each location you are installing a Smart Hub for Business.
                                            </Notification>
                                        )}
                                        <div className="install-fee-checkbox">
                                            <Field
                                                name='installFeeAcknowledgment'
                                                type='checkbox'
                                                label={t("INSTALL_FEE_ACKNOWLEDGMENT")}
                                                touched={touched}
                                                errors={errors}
                                                onChange={(e) =>
                                                    setValues({
                                                        ...values,
                                                        installFeeAcknowledgment: e.target.checked
                                                    })
                                                }
                                            />
                                        </div>
                                        <Field
                                            name='additionalInfo'
                                            type='textNote'
                                            label={t("ADDITIONAL_INFO")}
                                            touched={touched}
                                            errors={errors}
                                            tooltip={
                                                <Tooltip copy='en'>
                                                    Technician will require access to phone rooms,
                                                    DMARC, on site contact
                                                </Tooltip>
                                            }
                                            onChange={(e) =>
                                                setValues({
                                                    ...values,
                                                    additionalInfo: e.target.value
                                                })
                                            }
                                        />
                                        <FlexGrid.Row horizontalAlign='center'>
                                            <FormCol md={6} xs={12}>
                                                <Button
                                                    type='submit'
                                                    variant='standard'
                                                    rank='main'
                                                >
                                                    {t(`submitButtonLabel`)}
                                                </Button>
                                            </FormCol>
                                        </FlexGrid.Row>
                                    </Box>
                                </Box>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Container>
    );
};

LandingPage.propTypes = {
    onSubmit: PropTypes.func
};

export default LandingPage;